import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { type ApiError, api, qs, session, unwrap } from '../api/client';
import { useAccounts, useAutomations } from '../api/hooks';
import type { Items, Media, MediaCheckRow, Page, Post } from '../api/types';
import { useSession } from '../auth/session';
import { dayAfter } from '../components/calendar';
import { DateTimePicker } from '../components/DateTimePicker';
import {
  Badge,
  DataTable,
  Empty,
  ErrorAlert,
  Field,
  fmtDate,
  Modal,
  PageHeader,
  useToast,
} from '../components/ui';
import { AutomationWizard, draftForAccount } from './Automations';
import { checkLabel, optimisable, optimiseImage } from './media-optimise';

interface Draft {
  accountId: string;
  name: string;
  caption: string;
  hashtags: string;
  media: string;
  automationId: string;
  /** PST-09: further profiles and their caption overrides (keyed by account id). */
  extraAccounts: string[];
  captions: Record<string, string>;
}
const emptyDraft: Draft = {
  accountId: '',
  name: '',
  caption: '',
  hashtags: '',
  media: '',
  automationId: '',
  extraAccounts: [],
  captions: {},
};
/** PST-07: an existing post back into the editor. */
const toDraft = (p: Post): Draft => ({
  accountId: p.accountId,
  name: p.name,
  caption: p.caption ?? '',
  hashtags: p.hashtags.join(' '),
  media: p.media.map((m) => m.url).join('\n'),
  automationId: p.automationId ?? '',
  extraAccounts: [],
  captions: {},
});

/** PST-09: the profiles a post goes to, with caption overrides; null when it is a single-profile post. */
export const targetsOf = (d: Draft): { accountId: string; caption?: string }[] | null =>
  d.extraAccounts.length === 0
    ? null
    : [
        { accountId: d.accountId },
        ...d.extraAccounts.map((id) =>
          d.captions[id]?.trim() ? { accountId: id, caption: d.captions[id] } : { accountId: id },
        ),
      ];

export const toBody = (d: Draft) => ({
  accountId: d.accountId,
  name: d.name,
  caption: d.caption,
  hashtags: d.hashtags.split(/[\s,]+/).filter(Boolean),
  media: d.media
    .split(/\n+/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((url) => ({ url, type: /\.(mp4|mov|webm)(\?|$)/i.test(url) ? 'video' : 'image' })),
  automationId: d.automationId || null,
});

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** PST-24: reads the post once a second until it leaves `publishing`, at most ten times. */
export async function pollPost(
  id: string,
  attempts = 10,
  waitMs = 1000,
  read: (id: string) => Promise<Post> = (pid) => unwrap(api.get<Post>(`/portal/posts/${pid}`)),
): Promise<Post> {
  const step = async (left: number): Promise<Post> => {
    await sleep(waitMs);
    const p = await read(id);
    return p.status !== 'publishing' || left <= 1 ? p : step(left - 1);
  };
  return step(attempts);
}

export function PostsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const { can } = useSession();
  const accounts = useAccounts();
  const automations = useAutomations();
  const [filter, setFilter] = useState({
    status: '',
    accountId: '',
    q: '',
    automationSet: '',
    from: '',
    to: '',
  });
  // PST-20: the list pages through the API's cursor
  const posts = useInfiniteQuery({
    queryKey: ['posts', filter],
    queryFn: ({ pageParam }) =>
      unwrap(
        // UI-60: the "Scheduled to" day is inclusive
        api.get<Page<Post>>(
          `/portal/posts${qs({
            ...filter,
            to: filter.to ? dayAfter(filter.to) : undefined,
            cursor: pageParam ?? undefined,
          })}`,
        ),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
  const postItems = posts.data?.pages.flatMap((pg) => pg.items) ?? [];
  const [editing, setEditing] = useState<Draft | null>(null);
  // a draft opened before the accounts loaded gets the first profile once they arrive
  useEffect(() => {
    const first = accounts.data?.items[0]?.id;
    if (first && editing && editing.accountId === '') setEditing({ ...editing, accountId: first });
  }, [accounts.data, editing]);
  const [editId, setEditId] = useState<string | null>(null); // PST-07: Edit on drafts and scheduled posts
  const [viewing, setViewing] = useState<Post | null>(null); // UI-40: the PRD's View action
  const media = useQuery({
    queryKey: ['all-media', accounts.data?.items.map((a) => a.id)],
    enabled: (accounts.data?.items.length ?? 0) > 0,
    queryFn: async () =>
      (
        await Promise.all(
          (accounts.data?.items ?? []).map((a) =>
            unwrap(api.get<Items<Media>>(`/portal/social-accounts/${a.id}/media`)).then(
              (r) => r.items,
            ),
          ),
        )
      ).flat(),
  });
  const mediaOf = (p: Post): Media | undefined =>
    media.data?.find((m) => m.accountId === p.accountId && m.externalId === p.externalMediaId);
  const triggersOf = (p: Post): number =>
    (automations.data?.items ?? []).filter(
      (a) =>
        a.accountId === p.accountId &&
        a.status !== 'draft' &&
        (a.mediaBinding.kind === 'any' ||
          (p.externalMediaId !== null && a.mediaBinding.mediaIds.includes(p.externalMediaId))),
    ).length;
  const edit = useMutation({
    mutationFn: (v: { id: string; d: Draft }) =>
      unwrap(
        api.patch<Post>(
          `/portal/posts/${v.id}`,
          (({ accountId: _a, ...rest }) => rest)(toBody(v.d)),
        ),
      ),
    onSuccess: async () => {
      await refresh();
      setEditing(null);
      setEditId(null);
      toast('Post updated');
    },
  });
  // UI-66: the wizard opened from a post dialog; the automation it creates is selected on return
  const [wizardFor, setWizardFor] = useState<'editing' | 'schedule' | null>(null);
  const flowsForWizard = useQuery({
    queryKey: ['flows'],
    queryFn: () => unwrap(api.get<Items<{ id: string; name: string }>>('/portal/flows')),
    enabled: wizardFor !== null,
  });
  const [schedule, setSchedule] = useState<{
    ids: string[];
    at: string;
    automationId: string;
  } | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['posts'] });
  // PST-22: each picked file is PUT as its own body; the answered url joins the media list
  const [uploading, setUploading] = useState(0);
  /** PST-22: the url the API answers for the bytes, or null (and a toast) when it refuses them. */
  const putUpload = async (file: Blob, name: string): Promise<string | null> => {
    const res = await fetch('/portal/uploads', {
      method: 'PUT',
      headers: { authorization: `Bearer ${session.get() ?? ''}`, 'content-type': file.type },
      body: file,
    }).catch(() => null);
    const body = (await res?.json().catch(() => null)) as {
      url?: string;
      error?: { message?: string };
    } | null;
    if (res?.ok && body?.url) return body.url;
    toast(
      body?.error?.message ?? `Upload of ${name} failed (${res?.status ?? 'network'})`,
      'error',
    );
    return null;
  };
  const uploadFiles = async (files: readonly File[]) => {
    setUploading((n) => n + files.length);
    for (const file of files) {
      const url = await putUpload(file, file.name);
      if (url)
        setEditing((d) =>
          d ? { ...d, media: [d.media.trim(), url].filter(Boolean).join('\n') } : d,
        );
      setUploading((n) => n - 1);
    }
  };
  // PST-25: the media list is checked shortly after it stops changing
  const mediaText = editing?.media ?? '';
  const [checkedText, setCheckedText] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setCheckedText(mediaText), 300);
    return () => clearTimeout(t);
  }, [mediaText]);
  const checkList = editing ? toBody({ ...editing, media: checkedText }).media : [];
  const mediaCheck = useQuery({
    queryKey: ['media-check', checkList],
    queryFn: () =>
      unwrap(api.post<Items<MediaCheckRow>>('/portal/posts/media-check', { media: checkList })),
    enabled: checkList.length > 0,
    placeholderData: (prev) => prev,
    retry: false,
  });
  // PST-25: an image re-encoded in the browser replaces its own line
  const [optimising, setOptimising] = useState<string | null>(null);
  const optimise = async (url: string) => {
    setOptimising(url);
    try {
      const res = await fetch(new URL(url, window.location.origin).pathname);
      if (!res.ok) throw new Error(`the image could not be read (${res.status})`);
      const next = await putUpload(await optimiseImage(await res.blob()), 'the optimised image');
      if (next) {
        setEditing((d) =>
          d
            ? {
                ...d,
                media: d.media
                  .split('\n')
                  .map((l) => (l.trim() === url ? next : l))
                  .join('\n'),
              }
            : d,
        );
        toast('Image optimised');
      }
    } catch (e) {
      toast(`Optimise failed: ${(e as Error).message}`, 'error');
    } finally {
      setOptimising(null);
    }
  };
  // PST-08
  const aiCaption = useMutation({
    mutationFn: (prompt: string) =>
      unwrap(api.post<{ caption: string }>('/portal/posts/caption', { prompt })),
    onSuccess: (r) => setEditing((d) => (d ? { ...d, caption: r.caption } : d)),
    onError: (e: ApiError) => toast(e.message, 'error'),
  });
  const create = useMutation({
    mutationFn: (d: Draft) =>
      unwrap(
        api.post<Post>('/portal/posts', {
          ...toBody(d),
          ...(targetsOf(d) ? { targets: targetsOf(d) } : {}),
        }),
      ),
    onSuccess: async () => {
      await refresh();
      setEditing(null);
      toast('Post saved as draft');
    },
  });
  const createAnd = useMutation({
    mutationFn: async (v: { d: Draft; action: 'publish' | 'schedule' }) => {
      // PST-09 / PST-19: several profiles -> several posts; the actions below apply to every one
      const targets = targetsOf(v.d);
      const created = targets
        ? (await unwrap(api.post<{ items: Post[] }>('/portal/posts', { ...toBody(v.d), targets })))
            .items
        : [await unwrap(api.post<Post>('/portal/posts', toBody(v.d)))];
      const p = created[0] as Post;
      // PST-19: with several profiles the action applies to every post created
      if (v.action === 'publish') {
        const published = await Promise.all(
          created.map((c) =>
            unwrap(
              api.post<Post>(`/portal/posts/${c.id}/publish`, {
                automationId: v.d.automationId || null,
              }),
            ),
          ),
        );
        return { ...(published[0] as Post), others: published.length - 1 };
      }
      setSchedule({ ids: created.map((c) => c.id), at: '', automationId: v.d.automationId });
      return { ...p, others: created.length - 1 };
    },
    onSuccess: async (p, v) => {
      await refresh();
      setEditing(null);
      if (v.action !== 'publish') return;
      // PST-24: a post still publishing is followed until Instagram answers
      if (p.status === 'publishing') return announce(p);
      toast(
        p.status === 'posted'
          ? `Published to Instagram${p.others > 0 ? ` (${p.others + 1} posts)` : ''}`
          : `Publish failed: ${p.lastError ?? 'unknown'}`,
        p.status === 'posted' ? 'ok' : 'error',
      );
    },
  });
  // PST-19: with several profiles every created post is scheduled
  const doSchedule = useMutation({
    mutationFn: async () => {
      const body = {
        at: new Date(schedule?.at ?? '').toISOString(),
        automationId: schedule?.automationId || null,
      };
      const done = await Promise.all(
        (schedule?.ids ?? []).map((id) =>
          unwrap(api.post<Post>(`/portal/posts/${id}/schedule`, body)),
        ),
      );
      return done[0] as Post;
    },
    onSuccess: async () => {
      await refresh();
      setSchedule(null);
      toast('Post scheduled');
    },
  });
  /** PST-24: Instagram answers later; the page asks every second for up to ten seconds. */
  const announce = async (p: Post) => {
    const settled = p.status === 'publishing' ? await pollPost(p.id) : p;
    await refresh();
    if (settled.status === 'posted') toast('Posted', 'ok', settled.permalink ?? undefined);
    else if (settled.status === 'failed')
      toast(`Publishing failed: ${settled.lastError ?? 'unknown reason'}`, 'error');
    else toast('Still publishing; the list updates when Instagram answers');
  };
  const publish = useMutation({
    mutationFn: (id: string) => unwrap(api.post<Post>(`/portal/posts/${id}/publish`)),
    onSuccess: announce,
    onError: (e: ApiError) => toast(e.message, 'error'),
  });
  // ADM-16: a failed post goes back to the schedule
  const retry = useMutation({
    mutationFn: (id: string) => unwrap(api.post<Post>(`/portal/posts/${id}/retry`)),
    onSuccess: async () => {
      await refresh();
      toast('Post queued again');
    },
    onError: (e: ApiError) => toast(e.message, 'error'),
  });
  const del = useMutation({
    mutationFn: (id: string) => unwrap(api.del(`/portal/posts/${id}`)),
    onSuccess: refresh,
    onError: (e: ApiError) => toast(e.message, 'error'),
  });
  const setAutomation = useMutation({
    mutationFn: (v: { id: string; automationId: string }) =>
      unwrap(api.patch(`/portal/posts/${v.id}`, { automationId: v.automationId || null })),
    onSuccess: refresh,
    onError: (e: ApiError) => toast(e.message, 'error'),
  });
  return (
    <>
      <PageHeader
        title="Posts & AutoDM schedule"
        subtitle="Create, schedule and publish posts, then attach a comment-to-DM automation"
        actions={
          can('automation:create') ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setEditId(null);
                setEditing({ ...emptyDraft, accountId: accounts.data?.items[0]?.id ?? '' });
              }}
            >
              New post
            </button>
          ) : null
        }
      />
      <div className="row" style={{ marginBottom: 12 }}>
        <input
          className="input"
          style={{ maxWidth: 220 }}
          placeholder="Search name or caption"
          value={filter.q}
          onChange={(e) => setFilter({ ...filter, q: e.target.value })}
        />
        <select
          className="input"
          style={{ maxWidth: 200 }}
          value={filter.accountId}
          onChange={(e) => setFilter({ ...filter, accountId: e.target.value })}
        >
          <option value="">All profiles</option>
          {(accounts.data?.items ?? []).map((a) => (
            <option key={a.id} value={a.id}>
              @{a.username}
            </option>
          ))}
        </select>
        <select
          className="input"
          style={{ maxWidth: 160 }}
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
        >
          <option value="">All statuses</option>
          {['draft', 'scheduled', 'publishing', 'posted', 'failed'].map((s) => (
            <option key={s} value={s}>
              {s === 'publishing' ? 'queued' : s}
            </option>
          ))}
        </select>
        <select
          className="input"
          style={{ maxWidth: 180 }}
          value={filter.automationSet}
          onChange={(e) => setFilter({ ...filter, automationSet: e.target.value })}
        >
          <option value="">Automation: any</option>
          <option value="true">Automation set</option>
          <option value="false">No automation</option>
        </select>
        <input
          className="input"
          style={{ maxWidth: 170 }}
          type="date"
          aria-label="Scheduled from"
          value={filter.from}
          onChange={(e) => setFilter({ ...filter, from: e.target.value })}
        />
        <input
          className="input"
          style={{ maxWidth: 170 }}
          type="date"
          aria-label="Scheduled to (inclusive)"
          title="Scheduled to (the day itself is included)"
          value={filter.to}
          onChange={(e) => setFilter({ ...filter, to: e.target.value })}
        />
      </div>
      <ErrorAlert error={posts.error} />
      <DataTable>
        <table className="table">
          <thead>
            <tr>
              <th>Post ID</th>
              <th>Name</th>
              <th>Profile</th>
              <th>Scheduled</th>
              <th>Status</th>
              <th>Triggers</th>
              <th>Automation</th>
              <th>Auto DMs sent</th>
              <th>Post analysis</th>
              <th>Instagram</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {postItems.map((p) => (
              <tr key={p.id}>
                <td className="mono">{p.id.slice(-8)}</td>
                <td>
                  <strong>{p.name}</strong>
                  <div className="small muted truncate">{p.caption}</div>
                </td>
                <td>@{accounts.data?.items.find((a) => a.id === p.accountId)?.username ?? '…'}</td>
                <td>{fmtDate(p.scheduledAt)}</td>
                <td>
                  <Badge value={p.status === 'publishing' ? 'queued' : p.status} />
                  {p.lastError ? <div className="small muted">{p.lastError}</div> : null}
                </td>
                <td>{triggersOf(p)}</td>
                <td>
                  <select
                    className="input"
                    value={p.automationId ?? ''}
                    disabled={p.status === 'posted' || p.status === 'publishing'}
                    onChange={(e) =>
                      setAutomation.mutate({ id: p.id, automationId: e.target.value })
                    }
                  >
                    <option value="">none</option>
                    {(automations.data?.items ?? []).map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                  {p.automations > 0 ? (
                    <div className="small muted">{p.automations} bound</div>
                  ) : null}
                </td>
                <td>{p.dmsSent}</td>
                <td>
                  {p.status === 'posted'
                    ? (() => {
                        const m = mediaOf(p);
                        return m ? (
                          <span className="small">
                            {m.likeCount ?? '—'} likes · {m.commentsCount ?? '—'} comments
                            {m.viewCount !== null ? ` · ${m.viewCount} views` : ''}
                            {m.shareCount !== null ? ` · ${m.shareCount} shares` : ''}
                          </span>
                        ) : (
                          <span className="small muted">sync the account to see insights</span>
                        );
                      })()
                    : '—'}
                </td>
                <td>
                  {p.permalink ? (
                    <a href={p.permalink} target="_blank" rel="noreferrer">
                      Open
                    </a>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="actions">
                  <button type="button" className="btn btn-sm" onClick={() => setViewing(p)}>
                    View
                  </button>{' '}
                  {p.status !== 'posted' &&
                  p.status !== 'publishing' &&
                  can('automation:update') ? (
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => {
                        setEditId(p.id);
                        setEditing(toDraft(p));
                      }}
                    >
                      Edit
                    </button>
                  ) : null}{' '}
                  {p.status !== 'posted' &&
                  p.status !== 'publishing' &&
                  can('automation:publish') ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={() => publish.mutate(p.id)}
                    >
                      Publish now
                    </button>
                  ) : null}{' '}
                  {p.status === 'failed' && can('automation:publish') ? (
                    <button
                      type="button"
                      className="btn btn-sm"
                      disabled={retry.isPending}
                      onClick={() => retry.mutate(p.id)}
                    >
                      Retry
                    </button>
                  ) : null}{' '}
                  {p.status !== 'posted' &&
                  p.status !== 'publishing' &&
                  can('automation:publish') ? (
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() =>
                        setSchedule({ ids: [p.id], at: '', automationId: p.automationId ?? '' })
                      }
                    >
                      Schedule
                    </button>
                  ) : null}{' '}
                  {p.status !== 'posted' &&
                  p.status !== 'publishing' &&
                  can('automation:delete') ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={() => del.mutate(p.id)}
                    >
                      Delete
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
            {posts.data && postItems.length === 0 ? (
              <tr>
                <td colSpan={11}>
                  <Empty>No posts yet.</Empty>
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </DataTable>
      {posts.hasNextPage ? (
        <p>
          <button
            type="button"
            className="btn"
            disabled={posts.isFetchingNextPage}
            onClick={() => posts.fetchNextPage()}
          >
            Load more
          </button>
        </p>
      ) : null}
      {editing ? (
        <Modal
          title={editId ? 'Edit post' : 'Create post'}
          onClose={() => {
            setEditing(null);
            setEditId(null);
          }}
          wide
          footer={
            <>
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setEditing(null);
                  setEditId(null);
                }}
              >
                Cancel
              </button>
              {editId ? (
                // PST-19: an edit saves the post it opened; scheduling and publishing live on the row
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={edit.isPending}
                  onClick={() => edit.mutate({ id: editId, d: editing })}
                >
                  Save changes
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn"
                    disabled={create.isPending}
                    onClick={() => create.mutate(editing)}
                  >
                    Save as draft
                  </button>
                  <button
                    type="button"
                    className="btn"
                    disabled={createAnd.isPending}
                    onClick={() => createAnd.mutate({ d: editing, action: 'schedule' })}
                  >
                    Schedule
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={createAnd.isPending}
                    onClick={() => createAnd.mutate({ d: editing, action: 'publish' })}
                  >
                    Publish now
                  </button>
                </>
              )}
            </>
          }
        >
          <ErrorAlert error={(create.error ?? createAnd.error) as ApiError | null} />
          <div className="row">
            <Field label="Post name">
              <input
                className="input"
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              />
            </Field>
            <Field label="Profile">
              <select
                className="input"
                value={editing.accountId}
                onChange={(e) => setEditing({ ...editing, accountId: e.target.value })}
              >
                {(accounts.data?.items ?? []).map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.platform} · @{a.username}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <Field
            label="Media URLs (one per line; images or videos, https)"
            help="Paste public https links to images or videos already hosted somewhere, or upload files below. Several items create a carousel."
          >
            <textarea
              className="input"
              value={editing.media}
              onChange={(e) => setEditing({ ...editing, media: e.target.value })}
              placeholder="https://cdn.example.com/photo.jpg"
            />
            {editing.media.trim() && (mediaCheck.data?.items.length ?? 0) > 0 ? (
              <ul className="media-checks small" aria-label="Media check">
                {(mediaCheck.data?.items ?? []).map((r, i) => (
                  <li key={`${i}-${r.url}`}>
                    <span className="mono" title={r.url}>
                      {r.url.slice(r.url.lastIndexOf('/') + 1)}
                    </span>{' '}
                    <span className="muted">{checkLabel(r)}</span>
                    {r.issues.map((issue) => (
                      <div
                        key={issue.code}
                        className={issue.severity === 'error' ? 'media-issue-error' : 'muted'}
                      >
                        {`${issue.severity === 'error' ? 'Error' : 'Warning'}: ${issue.message}`}
                      </div>
                    ))}
                    {optimisable(r) ? (
                      <button
                        type="button"
                        className="btn btn-sm"
                        disabled={optimising !== null}
                        onClick={() => void optimise(r.url)}
                      >
                        {optimising === r.url ? 'Optimising…' : 'Optimise'}
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : null}
          </Field>
          <Field
            label="Upload from your computer"
            help={
              uploading > 0
                ? `Uploading ${uploading} file${uploading === 1 ? '' : 's'}…`
                : 'JPEG or PNG up to 8 MB, MP4 or MOV up to 100 MB; each file is added to the list above'
            }
          >
            <input
              className="input"
              type="file"
              aria-label="Upload from your computer"
              accept="image/jpeg,image/png,video/mp4,video/quicktime"
              multiple
              disabled={uploading > 0}
              onChange={(e) => {
                const files = [...(e.target.files ?? [])];
                e.target.value = '';
                if (files.length > 0) void uploadFiles(files);
              }}
            />
          </Field>
          <Field
            label="Caption"
            help="Up to 2200 characters. Draft with AI uses the key from Settings → AI configuration."
          >
            <textarea
              className="input"
              value={editing.caption}
              onChange={(e) => setEditing({ ...editing, caption: e.target.value })}
            />
            <button
              type="button"
              className="btn btn-sm"
              disabled={aiCaption.isPending}
              onClick={() => {
                const prompt = window.prompt('Describe the post for the AI caption', editing.name);
                if (prompt) aiCaption.mutate(prompt);
              }}
            >
              Draft with AI
            </button>
          </Field>
          {!editId && (accounts.data?.items.length ?? 0) > 1 ? (
            <Field label="Also post to (one post per profile)" group>
              <div className="pill-row">
                {(accounts.data?.items ?? [])
                  .filter((a) => a.id !== editing.accountId)
                  .map((a) => (
                    <label key={a.id} className="checkbox small">
                      <input
                        type="checkbox"
                        checked={editing.extraAccounts.includes(a.id)}
                        onChange={(e) =>
                          setEditing({
                            ...editing,
                            extraAccounts: e.target.checked
                              ? [...editing.extraAccounts, a.id]
                              : editing.extraAccounts.filter((x) => x !== a.id),
                          })
                        }
                      />{' '}
                      @{a.username}
                    </label>
                  ))}
              </div>
              {editing.extraAccounts.map((id) => (
                <Field
                  key={id}
                  label={`Caption for @${accounts.data?.items.find((a) => a.id === id)?.username ?? id} (blank = shared caption)`}
                >
                  <textarea
                    className="input"
                    value={editing.captions[id] ?? ''}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        captions: { ...editing.captions, [id]: e.target.value },
                      })
                    }
                  />
                </Field>
              ))}
            </Field>
          ) : null}
          <div className="row">
            <Field label="Hashtags">
              <input
                className="input"
                value={editing.hashtags}
                onChange={(e) => setEditing({ ...editing, hashtags: e.target.value })}
                placeholder="#summer #drop"
              />
            </Field>
            <Field label="Comment-to-DM automation (optional)">
              <select
                className="input"
                value={editing.automationId}
                onChange={(e) => setEditing({ ...editing, automationId: e.target.value })}
              >
                <option value="">Just publish</option>
                {(automations.data?.items ?? []).map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
              {can('automation:create') ? (
                <button
                  type="button"
                  className="btn btn-sm"
                  style={{ marginTop: 6 }}
                  onClick={() => setWizardFor('editing')}
                >
                  New automation…
                </button>
              ) : null}
            </Field>
          </div>
        </Modal>
      ) : null}
      {viewing ? (
        <Modal
          title="Post details"
          onClose={() => setViewing(null)}
          footer={
            <button type="button" className="btn" onClick={() => setViewing(null)}>
              Close
            </button>
          }
        >
          <dl className="kv">
            <dt>Name</dt>
            <dd>{viewing.name}</dd>
            <dt>Caption</dt>
            <dd>{viewing.caption}</dd>
            <dt>Hashtags</dt>
            <dd>{viewing.hashtags.map((h) => `#${h}`).join(' ') || '—'}</dd>
            <dt>Media</dt>
            <dd>
              {viewing.media.map((m) => (
                <div key={m.url}>
                  {m.type}: {m.url}
                </div>
              ))}
            </dd>
            <dt>Status</dt>
            <dd>{viewing.status}</dd>
            <dt>Scheduled for</dt>
            <dd>{viewing.scheduledAt ?? '—'}</dd>
            <dt>Published</dt>
            <dd>{viewing.publishedAt ?? '—'}</dd>
            <dt>Permalink</dt>
            <dd>
              {viewing.permalink ? (
                <a href={viewing.permalink} target="_blank" rel="noreferrer">
                  {viewing.permalink}
                </a>
              ) : (
                '—'
              )}
            </dd>
            <dt>DMs sent</dt>
            <dd>{viewing.dmsSent}</dd>
            {viewing.lastError ? (
              <>
                <dt>Last error</dt>
                <dd>{viewing.lastError}</dd>
              </>
            ) : null}
          </dl>
        </Modal>
      ) : null}
      {schedule ? (
        <Modal
          title="Schedule post"
          onClose={() => setSchedule(null)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setSchedule(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!schedule.at || doSchedule.isPending}
                onClick={() => doSchedule.mutate()}
              >
                Schedule
              </button>
            </>
          }
        >
          <ErrorAlert error={doSchedule.error as ApiError | null} />
          <Field label="Date and time">
            <DateTimePicker
              value={schedule.at}
              onChange={(at) => setSchedule({ ...schedule, at })}
            />
          </Field>
          <Field label="Comment-to-DM automation (optional)">
            <select
              className="input"
              value={schedule.automationId}
              onChange={(e) => setSchedule({ ...schedule, automationId: e.target.value })}
            >
              <option value="">Just publish</option>
              {(automations.data?.items ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
            {can('automation:create') ? (
              <button
                type="button"
                className="btn btn-sm"
                style={{ marginTop: 6 }}
                onClick={() => setWizardFor('schedule')}
              >
                New automation…
              </button>
            ) : null}
          </Field>
        </Modal>
      ) : null}
      {wizardFor ? (
        <AutomationWizard
          initial={draftForAccount(
            (wizardFor === 'editing' ? editing?.accountId : null) ??
              postItems.find((p) => schedule?.ids.includes(p.id))?.accountId ??
              accounts.data?.items[0]?.id ??
              '',
          )}
          existing={null}
          accounts={accounts.data?.items ?? []}
          flows={(flowsForWizard.data?.items ?? []) as never}
          hint="This automation applies to every post of the account; the post you are creating is bound to it when it is published."
          onCreated={(a) => {
            if (wizardFor === 'editing') setEditing((d) => (d ? { ...d, automationId: a.id } : d));
            else setSchedule((sc) => (sc ? { ...sc, automationId: a.id } : sc));
          }}
          onClose={() => setWizardFor(null)}
        />
      ) : null}
    </>
  );
}
